/**
 * Names the rows an ArTable-triggered workflow run is allowed to touch. Returns the
 * `WorkflowRowScope` `executeWorkflow` now requires from every caller that sends a
 * `sourceTableId`: the engine fails closed without one rather than sweeping the whole
 * rows collection, which is what duplicated ten rows on a one-row split (SYD-2322).
 *
 * A workflow with `includeHiddenRows` declares `{ allRows: true }` and skips the row
 * resolution entirely — the engine ignores the scope there, so the paginated fetch
 * would be pure waste. Everything else goes through `resolveArTableVisibleRows`, the
 * same resolver the XLSX export runs.
 *
 * Returns `null` on all three refusals, every one of them a case where naming the
 * visible rows is impossible or pointless: the org parameters the active view's hide
 * flags live on could not be read, the row source came back truncated, or nothing is
 * visible at all. Each explains itself through `messageApi`; the caller aborts.
 *
 * Holds no form subscription: `splitQuery` and the form type are click-time values,
 * read with `snapshot(formStore.state)` inside the callback (SYD-2252/2264).
 */

import { useCallback, useContext, useMemo } from 'react';
import { snapshot } from 'valtio';
import type { Snapshot } from 'valtio';
import { formStore } from '@/store';
import { MessageContext } from '@/providers/MessageContext';
import { useStoreNonReactive } from '@/store/useStoreNonReactive';
import { useVisibilityMode } from '@/use/useVisibilityMode';
import { useOrganizationParameters } from '@/use/useOrganizationParameters';
import { resolveExportVisibilityGate } from '@/use/resolveExportVisibilityGate';
import { ArTableUIStateContext, ArTableViewSortContext } from '../context';
import { resolveArTableRowsOrderBy } from '../resolveArTableRowsOrderBy';
import { resolveArTableVisibleRows } from './resolveArTableVisibleRows';
import { AR_TABLE_MAX_ROWS } from '#types/System.Parameters.types';
import type { ArColumnDefinition, ArTableId, ArTableType, TypeId } from '#types/System.Artifacts.types';
import type { SplitQuery } from '#types/System.types';
import type { Workflow, WorkflowRowScope } from '#types';
import type { DocumentState } from '@/store/Store.types';

type UseArTableWorkflowRowScopeParams = {
  tableId: ArTableId;
  parentPath: string;
};

type UseArTableWorkflowRowScopeReturn = {
  /** `null` means the visible rows are unknowable; the caller must not run the workflow. */
  resolveRowScope: (workflow: Workflow) => Promise<WorkflowRowScope | null>;
};

function useArTableWorkflowRowScope({
  tableId,
  parentPath,
}: UseArTableWorkflowRowScopeParams): UseArTableWorkflowRowScopeReturn {
  const { messageApi } = useContext(MessageContext);
  const visibilityMode = useVisibilityMode();
  const uiState = useContext(ArTableUIStateContext);
  const viewSortCtx = useContext(ArTableViewSortContext);
  const {
    data: orgParams,
    loading: orgParamsLoading,
    error: orgParamsError,
    getVisibilityTypesForFormType,
  } = useOrganizationParameters();

  const { state: schemaState } = useStoreNonReactive('schema');
  const { state: rowsState } = useStoreNonReactive('rows');

  const rowsOrderBy = useMemo(() => {
    return resolveArTableRowsOrderBy(viewSortCtx?.viewSort);
  }, [viewSortCtx?.viewSort]);

  const resolveRowScope = useCallback(
    async (workflow: Workflow): Promise<WorkflowRowScope | null> => {
      if (workflow.includeHiddenRows === true) return { allRows: true };

      const gate = resolveExportVisibilityGate({
        visibilityMode,
        orgParams,
        orgParamsLoading,
        orgParamsError,
      });
      if (gate) {
        messageApi[gate.severity](gate.message);
        return null;
      }

      const live = snapshot(formStore.state);
      const visibilityType = getVisibilityTypesForFormType(live.formType.data?.docId)
        .find((v) => v.id === visibilityMode);

      const schemaSnap = snapshot(schemaState) as Snapshot<DocumentState<ArTableType>>;
      const columns = (schemaSnap.data?.columns ?? []) as ArColumnDefinition[];

      const filterEntries = Object.entries(uiState ? snapshot(uiState.filtering).byColumnId : {}).filter(
        (entry): entry is [TypeId, string] => typeof entry[1] === 'string' && entry[1].length > 0,
      );

      const paginationSnap = uiState ? snapshot(uiState.pagination) : null;

      const { visibleRowIds, isTruncated } = await resolveArTableVisibleRows({
        tableId,
        parentPath,
        rowsOrderBy,
        isPaginated: (paginationSnap?.totalPages ?? 0) > 1,
        totalRows: paginationSnap?.totalRows ?? 0,
        rowsState,
        columns,
        splitQuery: live.splitQuery as SplitQuery | undefined,
        filterEntries,
        visibilityMode,
        visibilityType,
        kitsEnabled: schemaSnap.data?.bundles?.kits?.enabled === true,
        groupsEnabled: schemaSnap.data?.bundles?.groups === true,
      });

      /**
       * Refuse rather than send a partial list: an `allRows` action would take the
       * subset for the whole table and report the truncated count as a success.
       */
      if (isTruncated) {
        messageApi.error(
          `This table holds more rows than a single workflow run can be scoped to, so the run was not started. Split or filter the table to fewer than ${AR_TABLE_MAX_ROWS} rows and try again.`,
        );
        return null;
      }

      if (visibleRowIds.length === 0) {
        messageApi.warning(
          'No rows are visible under the current split and filters, so there is nothing for this workflow to act on.',
        );
        return null;
      }

      return { visibleRowIds };
    },
    [
      getVisibilityTypesForFormType,
      messageApi,
      orgParams,
      orgParamsError,
      orgParamsLoading,
      parentPath,
      rowsOrderBy,
      rowsState,
      schemaState,
      tableId,
      uiState,
      visibilityMode,
    ],
  );

  return { resolveRowScope };
}

export { useArTableWorkflowRowScope };
export type { UseArTableWorkflowRowScopeParams, UseArTableWorkflowRowScopeReturn };
